import React, { useState } from "react";
import { View, Pressable, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { displayName } from "./types";
import { TableStart } from "./TableStart";
import { TableEnd } from "./TableEnd";

export interface GroupBannerProps {
  field: string;
  value: string;
  count?: number;
  depth: number;
  continued?: boolean;
  selectable?: boolean;
  headerFunc?: (s: string) => string;
  indicatorColor?: string;
  readonlyCallback?: (key: string, value: any, row?: any) => void;
  rawValue?: any;
  rowData?: any;
}

const INDENT_PER_LEVEL = 16;

export function GroupBanner({
  field,
  value,
  count,
  depth,
  continued,
  selectable,
  headerFunc = displayName,
  indicatorColor,
  readonlyCallback,
  rawValue,
  rowData,
}: GroupBannerProps) {
  const { colors } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const label = `${headerFunc(field)}: ${value}`;
  const suffix = continued ? ` (continued${count ? `, ${count} on this page` : ""})` : count ? ` (${count})` : "";

  const interactive = !!readonlyCallback;

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: colors.backgroundSecondary,
          borderColor: colors.border,
          paddingLeft: depth * INDENT_PER_LEVEL,
        },
      ]}
    >
      <TableStart selectable={selectable} borderColor={colors.border} />
      {indicatorColor && <View style={[styles.indicator, { backgroundColor: indicatorColor }]} />}
      <Pressable
        style={styles.content}
        onPress={interactive ? () => readonlyCallback(field, rawValue, rowData) : undefined}
        onHoverIn={interactive ? () => setIsHovered(true) : undefined}
        onHoverOut={interactive ? () => setIsHovered(false) : undefined}
        disabled={!interactive}
      >
        <ThemedText
          type="defaultSemiBold"
          style={[
            styles.text,
            { color: colors.textSecondary },
            isHovered && interactive && { textDecorationLine: "underline" },
          ]}
        >
          {label}
          {suffix}
        </ThemedText>
      </Pressable>
      <TableEnd borderColor={colors.border} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 6,
    borderWidth: StyleSheet.hairlineWidth,
    flexDirection: "row",
    height: 38,
    alignSelf: "flex-start",
    maxWidth: "100%",
    width: "100%",
    alignItems: "center",
  },
  content: {
    flex: 1,
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  text: {
    fontSize: 14,
  },
  indicator: {
    width: 4,
    height: 22,
    borderRadius: 2,
    marginLeft: 4,
  },
});
